import React, { useMemo, useEffect, useCallback, useRef, useContext } from 'react'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import { convertToBackendStr, parseBackendDate, addDay } from 'services/dates'
import {
    CreateRegistrationFormRequest,
    QuestionBlockOption,
    RegistrationBlock,
    RegistrationBlockData,
    RegistrationForm,
    UpdateRegistrationFormRequest,
} from 'services/api/registrations'
import {
    CampaignState,
    CAMPAIGN_STATUS_ENUM,
    publishCampaign,
    selectEditingCampaign,
    submitCampaign,
} from 'state/campaigns'
import {
    createForm,
    getForm,
    selectRegistrationForm,
    updateForm,
    updateRegistrationSchedule,
} from 'state/registrations'
import { selectRegistrationLengthLimits } from 'state/constants'
import { TaskSections } from '..'
import SetUpRegistration from './SetUpRegistration'
import SaveAndSubmitButtonGroup from '../../SaveAndSubmitButtonGroup'
import RegistrationFormBuilder from './RegistrationFormBuilder'
import RegistrationSchedule from './RegistrationSchedule'
import { BLOCK_TYPE } from 'state/registrations'
import { baseValidationSchema } from './baseValidationSchema'
import { selectorEditorErrors } from 'state/error'
import { runValidateSchema, setInitialTouched, validateFieldsAreAllPassed } from 'components/utils'
import { ERROR_MESSAGES } from './errorMessages'
import { ERROR_MESSAGES as BASE_ERROR_MESSAGE } from '../errorMessages'
import OnFormikValidationError from 'components/OnFormikValidationError'
import { showSnackBar } from 'services/toast'
import MaskWrapper from 'components/Campaign/Editor/MaskWrapper'
import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { ViewTypeContext, EDITOR_ROUTE_ENUM } from 'components/Campaign'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'
import { selectIsExternalUser } from 'state/profile'
import { selectTasksEarliestStartAndEndDate } from 'state/tasks'
interface BlockOptionField extends Omit<QuestionBlockOption, 'index' | 'uuid'> {
    index?: number
    uuid?: string
}

interface BlockDataField extends RegistrationBlockData {
    isMultiAnswer?: boolean
}

export interface BlockField extends Omit<RegistrationBlock, 'index' | 'options'> {
    hasOthers?: boolean
    existingOtherOption?: QuestionBlockOption
    data: BlockDataField
    options: BlockOptionField[]
}

export interface EditorValues extends Omit<RegistrationForm, 'uuid' | 'blocks'> {
    blocksData: Record<string, BlockField>
    blocksSorting: string[]
}

interface RegistrationEditorProps {
    selectedTaskSection: TaskSections
    taskUuid?: string
}

const Editor: React.FC<RegistrationEditorProps> = ({ selectedTaskSection, taskUuid }) => {
    switch (selectedTaskSection) {
        case TaskSections.SETUP:
            return <SetUpRegistration taskUuid={taskUuid} />
        case TaskSections.STEPS:
            return <RegistrationFormBuilder />
        case TaskSections.SCHEDULE:
            return <RegistrationSchedule taskUuid={taskUuid} />
        default:
            return <></>
    }
}

const RegistrationEditor: React.FC<RegistrationEditorProps> = ({ selectedTaskSection }) => {
    const [t] = useTranslation()
    const isPublish = useRef(true)
    const dispatch = useDispatch()
    const editingCampaign = useSelector(selectEditingCampaign)
    const settings = useSelector(selectRegistrationLengthLimits)
    const registrationForm = useSelector(selectRegistrationForm)
    const canPublishCampaign =
        (editingCampaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            editingCampaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            editingCampaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const viewType = useContext(ViewTypeContext)
    const errors = useSelector(selectorEditorErrors(['registration']))
    const isExternalUser = useSelector(selectIsExternalUser)
    const tasksEarliestStartAndEndDate = useSelector(selectTasksEarliestStartAndEndDate)
    const isAbleToReview =
        usePermission(CampaignStatusControl.ReviewCampaign) &&
        editingCampaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isAbleToLaunch =
        (editingCampaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            editingCampaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            editingCampaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    // Remove max_slots as it is in Section 1
    const registrationErrors = useMemo(() => {
        if (!errors) return {} as Record<string, any>
        const result = _.cloneDeep<Record<string, any>>(errors['registration'])
        if (result) delete result['max_slots']

        return result
    }, [errors])

    const callPublishCampaign = useCallback((): void => {
        if (!editingCampaign) return
        if (canPublishCampaign) {
            dispatch(
                //launch campaign
                publishCampaign({
                    data: {
                        campaign: {} as CampaignState,
                        uuid: editingCampaign.uuid,
                    },
                }),
            )
        } else {
            dispatch(
                //submit to pending
                submitCampaign({
                    data: {
                        campaign: {} as CampaignState,
                        uuid: editingCampaign.uuid,
                    },
                }),
            )
        }
    }, [editingCampaign])

    const createOrUpdateRegistration = useCallback(
        (data: RegistrationForm) => {
            if (!editingCampaign) return
            const payload: UpdateRegistrationFormRequest | CreateRegistrationFormRequest = {
                uuid: editingCampaign.uuid,
                data,
            }
            if (isPublish.current) {
                payload['onSuccessCallback'] = callPublishCampaign
            }
            if (registrationForm) {
                dispatch(updateForm(payload as UpdateRegistrationFormRequest))
            } else {
                dispatch(createForm(payload as CreateRegistrationFormRequest))
            }
        },
        [registrationForm, editingCampaign],
    )

    useEffect(() => {
        if (editingCampaign) {
            dispatch(getForm({ uuid: editingCampaign.uuid as string }))
        }
    }, [dispatch, editingCampaign])

    if (!editingCampaign) return <></>

    const initialValues = useMemo(() => {
        const result = { ...registrationForm } as EditorValues
        if (registrationForm) {
            const { blocks, time_window } = registrationForm
            const computedBlocks = blocks.map((block: any) => {
                if (block.type === BLOCK_TYPE.QUESTION) {
                    if (block.data.question_number?.min === 1 && block.data.question_number?.max === 1) {
                        block.data.isMultiAnswer = false
                    } else {
                        block.data.isMultiAnswer = true
                    }
                    const otherOptionObject = block.options.find((option: QuestionBlockOption) => option.is_others)
                    if (otherOptionObject) {
                        block.hasOthers = true
                        block.existingOtherOption = otherOptionObject
                        block.options = block.options.filter((option: QuestionBlockOption) => !option.is_others)
                    } else {
                        block.hasOthers = false
                    }
                }
                return block
            })
            result.time_window = {
                start: time_window ? parseBackendDate(time_window?.start, editingCampaign.country) : '',
                end: time_window
                    ? parseBackendDate(time_window?.end, editingCampaign.country, {
                          showTime: false,
                          showYear: true,
                          addSeconds: -1,
                      })
                    : '',
            }
            result.blocksData = _.keyBy(computedBlocks, 'uuid')
            result.blocksSorting = blocks.map((block: any) => block.uuid)
            // default true, when registration form return null/undefine
            result.enable_collect_follower_count = registrationForm.enable_collect_follower_count ?? true
        }
        return result
    }, [registrationForm])

    const formErrors = useMemo(() => {
        if (!registrationErrors) return {}
        const result = _.cloneDeep<Record<string, any>>(registrationErrors)

        Object.keys(result).forEach((key: string) => {
            const errorCode = result[key]
            if (!errorCode) return

            if (key === 'time_window' && errorCode) {
                Object.keys(errorCode).forEach(startOrEnd => {
                    const code = errorCode[startOrEnd]
                    result[key][startOrEnd] = BASE_ERROR_MESSAGE.time_window[startOrEnd][code] || code
                })
            } else if (key === 'blocks_required' && errorCode) {
                if (typeof errorCode === 'string') {
                    result['blocksSorting'] = ERROR_MESSAGES.blocksData[errorCode]
                    delete result['blocks_required']
                }
            } else if (key === 'blocks' && errorCode) {
                const _key = 'blocksData'
                result[_key] = {}
                Object.keys(result[key]).forEach(blockUuid => {
                    result[_key][blockUuid] = {}
                    Object.keys(result[key][blockUuid]).forEach(blockContent => {
                        const code = result[key][blockUuid][blockContent]
                        if (blockContent === 'options') {
                            result[_key][blockUuid][blockContent] = []
                            initialValues &&
                                initialValues.blocksData &&
                                initialValues.blocksData[blockUuid].options.forEach(option => {
                                    if (option && option.uuid && option.uuid in code) {
                                        const _errorCode = code[option.uuid]['option']
                                        try {
                                            result[_key][blockUuid][blockContent].push({
                                                option: ERROR_MESSAGES.blocksData[blockContent][_errorCode],
                                            })
                                        } catch {
                                            result[_key][blockUuid][blockContent].push(code[option.uuid])
                                        }
                                    } else {
                                        result[_key][blockUuid][blockContent].push('')
                                    }
                                })
                        } else if (blockContent === 'data') {
                            result[_key][blockUuid][blockContent] = {}
                            Object.keys(result[key][blockUuid][blockContent]).forEach(dataContent => {
                                if (dataContent === 'question_number') {
                                    result[_key][blockUuid][blockContent][dataContent] = {}
                                    Object.keys(result[key][blockUuid][blockContent][dataContent]).forEach(
                                        questionNumberContent => {
                                            const errorCode =
                                                result[key][blockUuid][blockContent][dataContent][questionNumberContent]
                                            try {
                                                result[_key][blockUuid][blockContent][dataContent][
                                                    questionNumberContent
                                                ] =
                                                    ERROR_MESSAGES.blocksData[blockContent][dataContent][
                                                        questionNumberContent
                                                    ][errorCode]
                                            } catch {
                                                result[_key][blockUuid][blockContent][dataContent][
                                                    questionNumberContent
                                                ] = errorCode
                                            }
                                        },
                                    )
                                } else {
                                    const errorCode = result[key][blockUuid][blockContent][dataContent]
                                    try {
                                        result[_key][blockUuid][blockContent][dataContent] =
                                            ERROR_MESSAGES.blocksData[blockContent][dataContent][errorCode]
                                    } catch {
                                        result[_key][blockUuid][blockContent][dataContent] = errorCode
                                    }
                                }
                            })
                        } else {
                            try {
                                let errorBlockContent = blockContent
                                if (
                                    initialValues &&
                                    initialValues.blocksData &&
                                    initialValues.blocksData[blockUuid] &&
                                    initialValues.blocksData[blockUuid].type === BLOCK_TYPE.QUESTION &&
                                    blockContent === 'title'
                                ) {
                                    errorBlockContent = 'questionTitle'
                                }

                                result[_key][blockUuid][blockContent] =
                                    ERROR_MESSAGES.blocksData[errorBlockContent][code]
                            } catch {
                                result[_key][blockUuid][blockContent] = code
                            }
                        }
                    })
                })
            } else if (ERROR_MESSAGES[key] && ERROR_MESSAGES[key][errorCode]) {
                result[key] = ERROR_MESSAGES[key][errorCode]
            }
        })
        return result
    }, [registrationErrors, initialValues])

    const initialTouched = useMemo(() => {
        const _errors = _.cloneDeep(formErrors)
        if (_errors) {
            return setInitialTouched(_errors)
        } else {
            return {}
        }
    }, [formErrors, registrationErrors])

    const formSchema = useMemo(() => {
        const compareTaskEarliestStartDate = tasksEarliestStartAndEndDate?.start
            ? parseBackendDate(tasksEarliestStartAndEndDate?.start, editingCampaign.country)
            : null
        const compareTaskEarliestEndDate = tasksEarliestStartAndEndDate?.end
            ? parseBackendDate(tasksEarliestStartAndEndDate?.end, editingCampaign.country, {
                  showTime: false,
                  showYear: true,
                  addSeconds: -1,
              })
            : null
        return baseValidationSchema(t, editingCampaign, settings, initialValues, isExternalUser, {
            start: compareTaskEarliestStartDate,
            end: compareTaskEarliestEndDate,
        })
    }, [editingCampaign, initialValues, isExternalUser, tasksEarliestStartAndEndDate])

    const validate: any = useCallback(
        async (values: any) => {
            const schemaErrors = await runValidateSchema(values, formSchema)
            const isInitialError = Object.keys(formErrors).length > 0
            if (isInitialError && isPublish.current) {
                const fieldsToValidate = [
                    'title',
                    'description',
                    'approval_expiration_days',
                    'time_window',
                    'blocksSorting',
                    'blocksData',
                ]
                const result = _.cloneDeep<Record<string, any>>(formErrors)

                fieldsToValidate.forEach(fieldName => {
                    if (result[fieldName] && values[fieldName]) {
                        if (fieldName === 'time_window' && fieldName in result) {
                            Object.keys(result[fieldName]).forEach(startOrEnd => {
                                if (!(fieldName in result)) {
                                    result[fieldName] = {}
                                }

                                if (
                                    schemaErrors &&
                                    fieldName in schemaErrors &&
                                    schemaErrors[fieldName] &&
                                    schemaErrors[fieldName][startOrEnd]
                                ) {
                                    result[fieldName][startOrEnd] = schemaErrors[fieldName][startOrEnd]
                                } else if (
                                    values &&
                                    fieldName in values &&
                                    values[fieldName] &&
                                    values[fieldName][startOrEnd]
                                ) {
                                    result[fieldName][startOrEnd] = ''
                                }
                            })
                        } else if (fieldName === 'blocksSorting' && fieldName in result) {
                            if (values && fieldName in values && values[fieldName].length > 0) {
                                result[fieldName] = ''
                            }
                        } else if (fieldName === 'blocksData' && fieldName in result) {
                            const blockSchemaErrors =
                                schemaErrors && fieldName in schemaErrors ? schemaErrors[fieldName] : null
                            Object.keys(result[fieldName]).forEach(blockUuid => {
                                Object.keys(result[fieldName][blockUuid]).forEach(blockContent => {
                                    if (blockContent === 'options') {
                                        if (
                                            blockSchemaErrors &&
                                            blockSchemaErrors[blockUuid] &&
                                            blockSchemaErrors[blockUuid][blockContent].length > 0
                                        ) {
                                            result[fieldName][blockUuid][blockContent] = [
                                                ...blockSchemaErrors[blockUuid][blockContent],
                                            ]
                                        } else if (
                                            values &&
                                            fieldName in values &&
                                            values[fieldName] &&
                                            values[fieldName][blockUuid] &&
                                            values[fieldName][blockUuid][blockContent].length > 0
                                        ) {
                                            values[fieldName][blockUuid][blockContent].forEach(
                                                (option: any, index: number) => {
                                                    if (option.option) {
                                                        result[fieldName][blockUuid][blockContent][index] = {
                                                            option: '',
                                                        }
                                                    }
                                                },
                                            )
                                        }
                                    } else if (blockContent === 'data') {
                                        Object.keys(result[fieldName][blockUuid][blockContent]).forEach(dataContent => {
                                            if (dataContent === 'question_number') {
                                                Object.keys(
                                                    result[fieldName][blockUuid][blockContent][dataContent],
                                                ).forEach(questionNumberContent => {
                                                    if (
                                                        blockSchemaErrors &&
                                                        blockSchemaErrors[blockUuid] &&
                                                        blockSchemaErrors[blockUuid][blockContent] &&
                                                        blockSchemaErrors[blockUuid][blockContent][dataContent] &&
                                                        blockSchemaErrors[blockUuid][blockContent][dataContent][
                                                            questionNumberContent
                                                        ]
                                                    ) {
                                                        result[fieldName][blockUuid][blockContent][dataContent][
                                                            questionNumberContent
                                                        ] =
                                                            blockSchemaErrors[blockUuid][blockContent][dataContent][
                                                                questionNumberContent
                                                            ]
                                                    } else if (
                                                        values &&
                                                        fieldName in values &&
                                                        values[fieldName] &&
                                                        values[fieldName][blockUuid] &&
                                                        values[fieldName][blockUuid][blockContent] &&
                                                        values[fieldName][blockUuid][blockContent][dataContent] &&
                                                        values[fieldName][blockUuid][blockContent][dataContent][
                                                            questionNumberContent
                                                        ]
                                                    ) {
                                                        result[fieldName][blockUuid][blockContent][dataContent][
                                                            questionNumberContent
                                                        ] = ''
                                                    }
                                                })
                                            } else {
                                                if (
                                                    blockSchemaErrors &&
                                                    blockSchemaErrors[blockUuid] &&
                                                    blockSchemaErrors[blockUuid][blockContent] &&
                                                    blockSchemaErrors[blockUuid][blockContent][dataContent]
                                                ) {
                                                    result[fieldName][blockUuid][blockContent][dataContent] =
                                                        blockSchemaErrors[blockUuid][blockContent][dataContent]
                                                } else if (
                                                    values &&
                                                    fieldName in values &&
                                                    values[fieldName] &&
                                                    values[fieldName][blockUuid] &&
                                                    values[fieldName][blockUuid][blockContent] &&
                                                    values[fieldName][blockUuid][blockContent][dataContent]
                                                ) {
                                                    result[fieldName][blockUuid][blockContent][dataContent] = ''
                                                }
                                            }
                                        })
                                    } else if (
                                        blockSchemaErrors &&
                                        blockSchemaErrors[blockUuid] &&
                                        blockSchemaErrors[blockUuid][blockContent]
                                    ) {
                                        result[fieldName][blockUuid][blockContent] =
                                            blockSchemaErrors[blockUuid][blockContent]
                                    } else if (
                                        values &&
                                        fieldName in values &&
                                        values[fieldName] &&
                                        values[fieldName][blockUuid]
                                    ) {
                                        if (blockContent === 'images') {
                                            if (values[fieldName][blockUuid][blockContent].length > 0) {
                                                result[fieldName][blockUuid][blockContent] = ''
                                            }
                                        } else if (values[fieldName][blockUuid][blockContent]) {
                                            result[fieldName][blockUuid][blockContent] = ''
                                        }
                                    }
                                })
                            })
                        } else {
                            if (schemaErrors && fieldName in schemaErrors && schemaErrors[fieldName]) {
                                result[fieldName] = schemaErrors[fieldName]
                            } else {
                                result[fieldName] = ''
                            }
                        }
                    }
                })

                return validateFieldsAreAllPassed(result) ? {} : result
            } else {
                return schemaErrors ? schemaErrors : {}
            }
        },
        [formErrors, formSchema],
    )

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            initialErrors={formErrors}
            initialTouched={initialTouched}
            validate={validate}
            onSubmit={(values: EditorValues): void => {
                const { blocksData, blocksSorting, ...formData } = values
                const dict = _.cloneDeep(blocksData)
                for (const uuid in dict) {
                    if (dict[uuid].type === BLOCK_TYPE.QUESTION) {
                        if (dict[uuid].data.isMultiAnswer === false)
                            dict[uuid].data.question_number = {
                                min: 1,
                                max: 1,
                            }
                        delete dict[uuid].data.isMultiAnswer
                        if (dict[uuid].hasOthers === true) {
                            if (dict[uuid].existingOtherOption) {
                                dict[uuid].options = dict[uuid].options.concat(
                                    dict[uuid].existingOtherOption as QuestionBlockOption,
                                )
                                delete dict[uuid].existingOtherOption
                            } else {
                                dict[uuid].options = dict[uuid].options.concat({
                                    option: 'other',
                                    is_others: true,
                                })
                            }
                        }
                        dict[uuid].options = dict[uuid].options.map((option: any, index: number) => ({
                            ...option,
                            is_others: option.is_others === undefined ? false : option.is_others,
                            index: index + 1,
                        }))
                        delete dict[uuid].hasOthers
                    }
                }
                const data = _.cloneDeep(formData) as RegistrationForm
                data.blocks = blocksSorting.map((uuid: string, index: number) => ({
                    ...dict[uuid],
                    index: index + 1,
                })) as RegistrationBlock[]
                data.time_window = {
                    start: data.time_window?.start
                        ? convertToBackendStr(data.time_window?.start, editingCampaign.country)
                        : '',
                    end: data.time_window?.end
                        ? convertToBackendStr(addDay(data.time_window?.end, 1), editingCampaign.country)
                        : '',
                }

                if (
                    CAMPAIGN_STATUS_ENUM.LIVE === editingCampaign.status &&
                    selectedTaskSection === TaskSections.SCHEDULE
                ) {
                    dispatch(
                        updateRegistrationSchedule({
                            uuid: data.uuid,
                            time_window: data.time_window,
                            approval_expiration_days: data.approval_expiration_days,
                        }),
                    )
                } else {
                    createOrUpdateRegistration(data)
                }
            }}
        >
            {(formikBag): JSX.Element => {
                return (
                    <CompareChangesPopup
                        initialData={formikBag.initialValues}
                        compareData={formikBag.values}
                        recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                    >
                        <Form>
                            <OnFormikValidationError
                                callback={() => {
                                    showSnackBar({
                                        title: t('campaign.template.createCampainSuccessfully.subText'),
                                        type: 'error',
                                    })
                                }}
                            />
                            <MaskWrapper>
                                <div className={'form-field'}>
                                    <Editor
                                        selectedTaskSection={selectedTaskSection}
                                        taskUuid={registrationForm?.uuid}
                                    />
                                </div>
                            </MaskWrapper>

                            {viewType === EDITOR_ROUTE_ENUM.EDIT && (
                                <EditCampaignRestricted
                                    campaignStatus={editingCampaign.status}
                                    isEditDeadline={selectedTaskSection === TaskSections.SCHEDULE}
                                    isAbleToReview={isAbleToReview}
                                    isAbleToLaunch={isAbleToLaunch}
                                >
                                    <SaveAndSubmitButtonGroup
                                        campaign={editingCampaign}
                                        disableSave={false}
                                        disableSubmit={!formikBag.isValid}
                                        onSubmit={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = true
                                            formikBag.submitForm()
                                        }}
                                        onSave={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = false
                                            formikBag.submitForm()
                                        }}
                                    />
                                </EditCampaignRestricted>
                            )}
                        </Form>
                    </CompareChangesPopup>
                )
            }}
        </Formik>
    )
}

export default RegistrationEditor
